import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import UniversalModal from './UniversalModal';
import { formatDateWithSeconds } from '../utils/dateUtils';

/**
 * Shared "pick an image from a tile/stack + set threshold" modal used by Audit logs
 * and Activity logs Find Person (search icon on tiles).
 */
export const FindPersonSearchFromTileModal = ({
  open,
  onClose,
  onSearch,
  loading = false,
  selectedImage,
  primary,
  stackImages = [],
  selectedItemId,
  onSelectItem,
  getCapturedTimestamp,
  threshold,
  onThresholdChange,
  introText,
  searchButtonLabel = 'Find Person',
  searchingButtonLabel = 'Searching...',
}) => (
  <UniversalModal
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    header={
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Select Image for Find Person Search</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
    }
    contentDividers
    footer={
      <>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSearch}
          disabled={loading || !selectedImage}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
          sx={{
            textTransform: 'none',
            backgroundColor: primary,
            '&:hover': {
              backgroundColor: primary,
              opacity: 0.9,
            },
          }}
        >
          {loading ? searchingButtonLabel : searchButtonLabel}
        </Button>
      </>
    }
  >
    <Stack spacing={3}>
      {introText ? (
        <Typography variant="body2" color="text.secondary">
          {introText}
        </Typography>
      ) : null}

      {stackImages.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Select Image ({stackImages.length} available)
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 2,
              maxHeight: '400px',
              overflowY: 'auto',
              p: 1,
            }}
          >
            {stackImages.map((item, idx) => {
              const isSelected = selectedItemId === item.uniqueId;
              const capturedTs = getCapturedTimestamp ? getCapturedTimestamp(item.log) : null;

              return (
                <Card
                  key={item.uniqueId || item.log?.audit_id || idx}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    border: isSelected ? `3px solid ${primary}` : '1px solid',
                    borderColor: isSelected ? primary : 'divider',
                    '&:hover': {
                      boxShadow: 3,
                      borderColor: primary,
                    },
                    transition: 'all 0.2s',
                  }}
                  onClick={() => onSelectItem?.(item)}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '100%',
                      bgcolor: '#f5f5f5',
                      overflow: 'hidden',
                    }}
                  >
                    {item.image ? (
                      <Box
                        component="img"
                        src={item.image}
                        alt="Bucket image"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: primary,
                          color: '#fff',
                          borderRadius: '50%',
                          width: 28,
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: primary,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    {item.isMain && (
                      <Chip
                        label="Main"
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          color: '#fff',
                          fontSize: '0.65rem',
                          height: 18,
                        }}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
                      {capturedTs?.iso ? formatDateWithSeconds(capturedTs.iso) : '-'}
                    </Typography>
                    {item.log?.audit_id && (
                      <Typography variant="caption" display="block" noWrap sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        ID: {item.log.audit_id}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minWidth: 'fit-content',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              lineHeight: '40px',
            }}
          >
            Similarity Threshold: {parseFloat(threshold || 0).toFixed(2)}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: 350, mt: 0.5 }}>
            <Slider
              value={parseFloat(threshold || 0.3)}
              onChange={(e, newValue) => onThresholdChange?.(String(newValue))}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: '0' },
                { value: 0.25, label: '0.25' },
                { value: 0.5, label: '0.5' },
                { value: 0.75, label: '0.75' },
                { value: 1, label: '1' },
              ]}
              sx={{
                width: '100%',
                '& .MuiSlider-root': {
                  padding: '8px 0 0 0',
                },
                '& .MuiSlider-thumb': {
                  width: 10,
                  height: 10,
                  marginTop: '-3px',
                },
                '& .MuiSlider-markLabel': {
                  fontSize: '0.65rem',
                  top: 20,
                },
                '& .MuiSlider-track': {
                  height: 4,
                  marginTop: '-2px',
                },
                '& .MuiSlider-rail': {
                  height: 4,
                  marginTop: '-2px',
                },
              }}
            />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Value between 0.00 and 1.00 (higher = stricter match)
        </Typography>
      </Box>
    </Stack>
  </UniversalModal>
);
